import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeSlash, GoogleLogo, ArrowRight } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { getApiBaseUrl } from '../utils/api';

export function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }
      
      const data = await response.json();
      toast.success(`Welcome back, ${data.user.name}!`);
      
      let dashboardUrl = '/dashboard';
      if (data.user.role === 'admin') {
        dashboardUrl = '/admin';
      } else if (data.user.role === 'staff') {
        dashboardUrl = '/staff';
      }
      navigate(dashboardUrl);
      
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1772480476588-b7980399da3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-background" />
        
        {/* Floating Content */}
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
              alt="STAY ON Logo" 
              className="h-14"
            />
          </Link>
          
          <div className="max-w-md">
            <h2 className="text-4xl font-black uppercase tracking-tight text-white mb-4">
              Win & Stay On
            </h2>
            <p className="text-lg text-white/70">
              Organized pickup basketball for grades 3-12. Real competition. Real development.
            </p>
          </div>
          
          <p className="text-sm text-white/50">
            © {new Date().getFullYear()} STAY ON Hoops
          </p>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_6652d962-fcde-40fe-ae1a-478daa9a079c/artifacts/xf9z325b_IMG_9879.png" 
                alt="STAY ON Logo" 
                className="h-14 mx-auto"
              />
            </Link>
          </div>
          
          {/* Header */}
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Welcome Back</p>
            <h1 className="text-4xl font-black uppercase tracking-tight mb-2">Sign In</h1>
            <p className="text-muted-foreground">Access your STAY ON Hoops account</p>
          </div>
          
          {/* Google Button */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-14 border-border hover:border-primary/50 hover:bg-primary/5 mb-6"
            data-testid="google-login-btn"
          >
            <GoogleLogo size={22} weight="bold" className="mr-3" />
            Continue with Google
          </Button>
          
          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-4 text-muted-foreground tracking-wider">Or continue with email</span>
            </div>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="text"
                inputMode="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="mt-2 h-12 bg-card border-border focus:border-primary"
                data-testid="email-input"
                placeholder="Enter your email"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  className="h-12 pr-12 bg-card border-border focus:border-primary"
                  data-testid="password-input"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <div className="flex justify-end">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 btn-primary text-base"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Signing In...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={20} weight="bold" className="ml-2" />
                </>
              )}
            </Button>
          </form>
          
          {/* Footer */}
          <p className="text-center text-sm text-muted-foreground mt-10">
            Don't have an account?{' '}
            <Link to="/register" className="text-primary font-medium hover:underline">
              Sign up here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
